import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import LoadingComponent from 'components/LoadingComponent';
import { CoursesTableToolbar } from 'components/courses/CoursesTableToolbar';
import { useCoursesPageStore } from 'components/courses/store';
import ImageBadge from 'components/images/ImageBadge';
import { Pagination } from 'components/reports/controls/Pagination';
import { TableBody } from 'components/tables/learner-stats/TableBody';
import { TableHeader } from 'components/tables/learner-stats/TableHeader';
import { SortArrow, TableHeaderContainer, TableHeaderText } from 'components/tables/learner-stats/elements';
import { Color } from 'core';
import { MemreButton, MemreFlexBox, MemreText } from 'components/core';
import CourseAssignmentCell from 'courses/components/CourseAssignmentCell';
import CoursePublishDateCell from 'courses/components/CoursePublishDateCell';
import { useCreateGroupCourse } from 'hooks/useCreateGroupCourse';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useGetCourses } from 'hooks/useGetCourses';
import _ from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { formatStringForSortComparison } from 'utils/tableUtils';

type Props = {
  groupId: string;
  close: () => void;
};

export const SelectableCoursesTable = ({ groupId, close }: Props) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const primaryPartnerId = user?.primaryPartner.id;
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<string>>(new Set());
  const {
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    setSortColumn,
    setSortDirection,
  } = useCoursesPageStore();
  const { data, status: queryStatus } = useGetCourses(primaryPartnerId, false);
  const [createGroupCourse] = useCreateGroupCourse(groupId);

  const courses = data?.data || [];
  const totalCount = data?.meta?.['total-count'] || 0;
  const totalPages = data?.meta?.['total-pages'] || 0;

  const getNameSpan = (name: string) => (
    <span style={{ textOverflow: 'ellipsis' }}>{_.truncate(name, { length: 40 })}</span>
  );

  const toggleSelectCourse = (courseId: string) => {
    setSelectedCourseIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  const getAssignmentCaption = (set: any) => {
    const suffix = ` ${t('in')} ${set.courseName}`;
    const captions = {
      set: t('Learning Set'),
      survey: t('Survey'),
      assessment: t('Assessment'),
      scorm: t('SCORM module'),
    };
    return captions[set.goalType] ? captions[set.goalType].concat(suffix) : '';
  };

  const toggleSelectAll = () => {
    const currentPageCourseIds = courses.map((course) => course.id);
    setSelectedCourseIds((prev) => {
      const allSelected = currentPageCourseIds.every((id) => prev.has(id));
      if (allSelected) {
        const newSet = new Set(prev);
        currentPageCourseIds.forEach((id) => newSet.delete(id));
        return newSet;
      } else {
        const newSet = new Set(prev);
        currentPageCourseIds.forEach((id) => {
          newSet.add(id);
        });
        return newSet;
      }
    });
  };

  const columns: ColumnDef<any>[] = [
    {
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          checked={courses.length > 0 && courses.every((course) => selectedCourseIds.has(course.id))}
          onChange={toggleSelectAll}
        />
      ),
      cell: ({ row }) => {
        const courseId = row.original.id;
        const isSelected = selectedCourseIds.has(courseId);

        return <input type="checkbox" checked={isSelected} onChange={() => toggleSelectCourse(courseId)} />;
      },
    },
    {
      id: 'name',
      accessorFn: (row) => row.name,
      cell: ({ row }) => {
        const rowData = row.original;
        const isCourseType = isCourse(rowData);
        return (
          <div style={{ display: 'flex' }}>
            <SCourseImageContainer style={{ flex: 1 }}>
              {isCourseType ? (
                <ImageBadge image={rowData.image} style={courseImageStyles} entity="course" />
              ) : (
                <FontAwesomeIcon
                  icon={faArrowUp}
                  style={{
                    display: 'inline',
                    color: Color.primaryBlack,
                    fontSize: '16px',
                    marginLeft: '10px',
                    top: '3px',
                    position: 'relative',
                    marginRight: '25px',
                  }}
                />
              )}
            </SCourseImageContainer>
            <span style={{ flex: 10 }}>
              {isCourseType ? (
                <Link
                  style={{ color: Color.primaryBlack, fontWeight: 500, textDecoration: 'underline' }}
                  to={`courses/${rowData.id}`}
                  tabIndex={0}
                >
                  {getNameSpan(rowData.name)}
                </Link>
              ) : (
                <span style={{ fontWeight: 600, color: Color.primaryBlack }}>{rowData.name}</span>
              )}
              {isCourseType && (
                <div style={{ fontSize: '.8em', marginTop: '.2em', color: Color.reportGray, textOverflow: 'ellipsis' }}>
                  {_.truncate(rowData.instructorNames.join(', '), { length: 40 }) || t('No Instructors Yet')}
                </div>
              )}
              {!isCourseType && (
                <div style={{ fontSize: '.8em', marginTop: '.2em', color: Color.reportGray }}>
                  {getAssignmentCaption(rowData)}
                </div>
              )}
            </span>
          </div>
        );
      },
      header: () => (
        <ColumnHeader
          title={t('Name')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('name')}
        />
      ),
    },
    {
      id: 'status',
      accessorFn: (row) => row.state,
      cell: ({ row }) => {
        const rowData = row.original;
        return isCourse(rowData) ? (
          <CoursePublishDateCell state={rowData.state} stateUpdatedAt={rowData.stateUpdatedAt} />
        ) : (
          <span />
        );
      },
      header: () => (
        <ColumnHeader
          title={t('Status')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('status')}
        />
      ),
    },
    {
      id: 'learners',
      accessorFn: (row) => row.studentUsersCount,
      cell: ({ row }) => <span>{row.original.studentUsersCount}</span>,
      header: () => <ColumnHeader title={t('Learners')} sort={''} sortDirection={sortDirection} onSort={() => {}} />,
    },
    {
      id: 'assignments',
      accessorFn: (row) => row.goalsCount,
      cell: ({ row }) => {
        const rowData = row.original;
        return rowData.type === 'limitedCourses' ? (
          <CourseAssignmentCell
            sharedAssignmentCount={rowData.sets?.filter((set: any) => set.isShared).length}
            assignmentCount={rowData.goalsCount}
          />
        ) : null;
      },
      header: () => <ColumnHeader title={t('Assignments')} sort={''} sortDirection={sortDirection} onSort={() => {}} />,
    },
    {
      id: 'last_updated',
      accessorFn: (row) => row.updatedAt,
      cell: ({ row }) => <p>{moment(row.original.updatedAt).format('MM/DD/YY')}</p>,
      header: () => (
        <ColumnHeader
          title={t('Last Updated')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('last_updated')}
        />
      ),
    },
    // ... Define other columns similar to CoursesTable, e.g., Name, Status, etc.
  ];

  const handleSortingChange = (sortBy: string) => {
    if (sortBy === sortColumn) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(sortBy);
      setSortDirection('asc');
    }
  };

  const table = useReactTable({
    data: courses,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const goToPageOne = () => setPageNumber(1);
  const goToNextPage = () => pageNumber < totalPages && setPageNumber(pageNumber + 1);
  const goToPreviousPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
  const goToLastPage = () => setPageNumber(totalPages);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  const handleHeaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const header = e.currentTarget.innerText.toLowerCase().replace(' ', '_');
    if (['learners', 'assignments'].includes(header)) {
      console.warn('Cannot sort by learners or assignments');
      return;
    }

    handleSortingChange(header);
  };

  const enrollSelectedCourses = () => {
    const courseIds = Array.from(selectedCourseIds);
    courseIds.forEach(async (courseId) => {
      await createGroupCourse({
        courseId,
        groupId,
      });
    });
    close();
  };

  return (
    <LoadingComponent
      isLoading={queryStatus === 'loading'}
      component={
        <div>
          <MemreButton
            text="Enroll Selected Courses"
            variant="contained"
            color="secondary"
            iconName="plus"
            onClick={enrollSelectedCourses}
          />
          <CoursesTableToolbar />
          <TableContainer>
            <StyledTable>
              <TableHeader table={table} handleHeaderClick={handleHeaderClick} />
              <TableBody table={table} />
            </StyledTable>
            <Pagination
              totalCount={totalCount}
              page={pageNumber}
              pageSize={pageSize}
              goToPageOne={goToPageOne}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
              goToLastPage={goToLastPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </TableContainer>
        </div>
      }
    />
  );
};

interface ColumnHeaderProps {
  title: string;
  sort: string;
  sortDirection: 'asc' | 'desc';
  onSort: () => void;
}

const ColumnHeader = ({ title, sort, sortDirection, onSort }: ColumnHeaderProps) => (
  <TableHeaderContainer
    style={Object.assign({ justifyContent: 'left' }, sort === '' ? {} : { cursor: 'pointer' })}
    onClick={onSort}
  >
    <TableHeaderText>{title}</TableHeaderText>
    {sort === formatStringForSortComparison(title) && (
      <SortArrow icon={sortDirection === 'desc' ? faArrowDown : faArrowUp} />
    )}
  </TableHeaderContainer>
);

const SCourseImageContainer = styled.div`
  display: inline-block;
`;

const courseImageStyles = {
  height: '32px',
  width: '32px',
  verticalAlign: 'middle',
  marginRight: '25px',
};

const TableContainer = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const isCourse = (record: any): boolean => record?.type === 'limitedCourses';
